import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, type SxProps, type Theme, Typography } from '@mui/material';
import { useSetAtom } from 'jotai';
import { nanoid } from 'nanoid';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { importModalAtom } from '@/atoms/import';
import { FormTip } from '@/components/FormTip/FormTip';
import { Divider } from '@/components/StyledComponents';
import { Text } from '@/components/ui/text';
import { Tooltip } from '@/components/ui/tooltip';
import type { ImportStatistics } from '@/types';
import { ImportExceptionsHead, ImportExceptionsRow, ImportStatisticsFound } from './ImportScript.styles';

const ImportException = ({ type, text, sx }: { type: string; text: string | null; sx?: SxProps<Theme> }) => {
    return (
        <ImportExceptionsRow>
            <div className="p-[10px]">
                <FormTip children={type} icon={<ErrorOutlineIcon />} sx={sx} />
            </div>
            <div className="p-[10px]">
                <Typography variant="body2" children={text || '-'} sx={theme => ({ color: theme.custom.c45 })} />
            </div>
        </ImportExceptionsRow>
    );
};

export const ImportScriptStatistics = ({ statistics, exceptions, notifications }: ImportStatistics) => {
    const { t } = useTranslation();
    const setImportModal = useSetAtom(importModalAtom);

    const isValid = !exceptions.length;

    const noСharacters = useMemo(() => {
        const foundsArr: { type: string; value: number }[] = [];
        const originalArray = Object.entries(statistics);
        for (const [key, value] of originalArray) {
            if (key !== 'characters' && key !== 'characters_similar' && !Array.isArray(value)) foundsArr.push({ type: key, value: value });
        }
        return foundsArr;
    }, [statistics]);

    const сharacters = useMemo(() => {
        const foundsArr: { type: string; value: string[] }[] = [];
        for (const found in statistics) {
            if (found === 'characters') foundsArr.push({ type: found, value: statistics.characters });
        }
        return foundsArr;
    }, [statistics]);

    const characters_similar = useMemo(() => {
        const foundsArr: { type: string; value: number }[] = [];
        for (const found in statistics) {
            if (found === 'characters_similar') foundsArr.push({ type: found, value: statistics.characters_similar });
        }
        return foundsArr;
    }, [statistics]);

    return (
        <div className="grid gap-[16px]">
            <div className="flex flex-col items-start gap-[4px]">
                <Text variant="subtitle4" color="text-inherit" children={t('common')} className="font-medium" />
                {noСharacters.map(({ type, value }) => (
                    <ImportStatisticsFound key={`${type}-${value}`}>
                        <Text variant="body1" color="text-inherit" children={`${t(`import.stat.${type}`)}:`} />
                        <Text variant="body1" color="text-inherit" children={value} />
                    </ImportStatisticsFound>
                ))}
                <Divider />
            </div>

            <div className="flex flex-col items-start gap-[4px] relative">
                <Text variant="subtitle4" color="text-inherit" children={t('character.characters')} className="font-medium" />
                {сharacters.map(({ type, value }) => (
                    <ImportStatisticsFound key={`${type}-${value}`}>
                        <Typography
                            variant="body1"
                            color={type === 'characters_similar' ? 'secondary.main' : 'inferit'}
                            children={`${t(`import.stat.${type}`)}:`}
                        />
                        <Typography variant="body1" color="inferit" children={value.length} />
                    </ImportStatisticsFound>
                ))}

                {characters_similar.map(({ type, value }) => (
                    <ImportStatisticsFound key={`${type}-${value}`}>
                        <Typography
                            variant="body1"
                            color={type === 'characters_similar' ? 'secondary.main' : 'inferit'}
                            children={`${t(`import.stat.${type}`)}:`}
                        />
                        <Typography variant="body1" color="inferit" children={value} />
                    </ImportStatisticsFound>
                ))}

                <Tooltip title={t('import.info.characters-tooltip')} placement="bottom" disableHoverListener={isValid}>
                    <Box
                        sx={{
                            right: '0',
                            position: 'absolute',
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            if (isValid) {
                                setImportModal(prev => ({
                                    ...prev,
                                    showCharacters: true,
                                    showStatistics: false,
                                    showRules: false,
                                    characters_union: null,
                                }));
                            }
                        }}
                    >
                        <ArrowForwardIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} color="secondary" />
                        <Typography
                            ml="5px"
                            letterSpacing="1.3px"
                            display="inline-block"
                            variant="subtitle2"
                            color={theme => theme.custom.c36}
                            children={t('import.characters-combine')}
                        />
                    </Box>
                </Tooltip>

                <Divider />
            </div>

            {exceptions.length || notifications.length ? (
                <div className="pb-[46px]">
                    <Text variant="subtitle4" color="text-inherit" children={t('errors')} className="block font-medium" />
                    <Typography
                        display="inline-block"
                        variant="body1"
                        color="inherit"
                        children={`${t('found-errors')}:`}
                        fontWeight="inherit"
                    />
                    <Typography
                        display="inline-block"
                        variant="body1"
                        color="inherit"
                        children={`${exceptions.length}`}
                        fontWeight="inherit"
                    />

                    <ImportExceptionsHead mt="10px">
                        <Typography variant="body1" color="inherit" children={t('error')} fontWeight="inherit" />
                        <Typography variant="body1" color="inherit" children={t('text')} fontWeight="inherit" />
                    </ImportExceptionsHead>

                    {exceptions.length
                        ? exceptions.map(e => (
                              <ImportException
                                  key={nanoid()}
                                  type={t(`import.error.${e.code}`)}
                                  text={e.text}
                                  sx={theme => ({ color: theme.palette.secondary.main })}
                              />
                          ))
                        : null}

                    {notifications.length
                        ? notifications.map(n => (
                              <ImportException
                                  key={nanoid()}
                                  type={t(`import.error.${n.code}`)}
                                  text={n.text}
                                  sx={theme => ({ color: theme.custom.c45 })}
                              />
                          ))
                        : null}
                </div>
            ) : null}
        </div>
    );
};
